import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AlertTriangle, X, BellRing } from "lucide-react";
import { useAuth } from "./auth";

const LS_KEY = "hedaply_credits_low";
const LS_NOTIFIED_AT = "hedaply_credits_notified_at";

const CreditAlertCtx = createContext({
  triggerCreditAlert: () => {},
  clearCreditAlert: () => {},
});

/**
 * Listens for 402 (credit-exhausted) signals from any AI endpoint and shows
 * a sticky owner-only banner + fires a browser/phone notification (works on
 * installed PWA on iOS 16.4+ and Android). Staff and customers see nothing.
 *
 * Usage:
 *   - Wrap the app with <CreditAlertProvider>
 *   - In any fetch error handler, call useCreditAlert().triggerCreditAlert()
 *     when you see HTTP 402 from a render/edit/variation/transcribe call.
 */
export function CreditAlertProvider({ children }) {
  const { user } = useAuth();
  const [low, setLow] = useState(() => localStorage.getItem(LS_KEY) === "1");
  const lastNotifAt = useRef(parseInt(localStorage.getItem(LS_NOTIFIED_AT) || "0", 10));

  // Ask owner for notification permission once, soft — fail silently if user denies
  useEffect(() => {
    if (user?.role !== "owner") return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      // Slight delay so it doesn't fire on first paint
      const t = setTimeout(() => {
        try {
          Notification.requestPermission().catch(() => {});
        } catch {
          /* old browsers */
        }
      }, 2500);
      return () => clearTimeout(t);
    }
  }, [user]);

  const fireNotification = useCallback(() => {
    try {
      if (typeof Notification === "undefined") return;
      if (Notification.permission !== "granted") return;
      // Throttle: at most once every 5 minutes per device, even if many 402s land
      const now = Date.now();
      if (now - lastNotifAt.current < 5 * 60 * 1000) return;
      lastNotifAt.current = now;
      localStorage.setItem(LS_NOTIFIED_AT, String(now));
      new Notification("Hedaply AI · Credits low", {
        body: "AI credits are exhausted. Please top up the Universal Key to resume renders.",
        icon: "/heda-icon-192.png",
        badge: "/heda-icon-192.png",
        tag: "hedaply-credits-low",
        requireInteraction: false,
      });
    } catch {
      /* notification failed — banner alone is enough */
    }
  }, []);

  const triggerCreditAlert = useCallback(() => {
    // Only owners see / get notified — staff never know
    if (user?.role !== "owner") return;
    const wasLow = localStorage.getItem(LS_KEY) === "1";
    localStorage.setItem(LS_KEY, "1");
    setLow(true);
    if (!wasLow) fireNotification();
  }, [user, fireNotification]);

  const clearCreditAlert = useCallback(() => {
    localStorage.removeItem(LS_KEY);
    setLow(false);
  }, []);

  const showBanner = user?.role === "owner" && low;

  return (
    <CreditAlertCtx.Provider value={{ triggerCreditAlert, clearCreditAlert }}>
      {showBanner && <CreditAlertBanner onDismiss={clearCreditAlert} />}
      {children}
    </CreditAlertCtx.Provider>
  );
}

export function useCreditAlert() {
  return useContext(CreditAlertCtx);
}

function CreditAlertBanner({ onDismiss }) {
  const granted = typeof Notification !== "undefined" && Notification.permission === "granted";
  return (
    <div
      data-testid="credit-alert-banner"
      className="sticky top-0 z-50 flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 shadow-sm sm:px-5"
    >
      <AlertTriangle size={16} className="shrink-0 text-amber-600" />
      <div className="flex-1 text-[12.5px] leading-snug sm:text-[13px]">
        <span className="font-semibold">AI credits low</span> · Please top up the Universal
        Key to resume renders.{" "}
        <span className="text-amber-700/80">(Only you see this — staff don't)</span>
      </div>
      {granted && (
        <span
          className="hidden items-center gap-1 rounded-full bg-white/70 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-amber-700 sm:inline-flex"
          title="Phone notifications enabled"
        >
          <BellRing size={10} /> Push on
        </span>
      )}
      <button
        type="button"
        data-testid="credit-alert-dismiss"
        onClick={onDismiss}
        className="rounded-full p-1 text-amber-700 transition hover:bg-amber-100"
        title="Dismiss (will return on next credit error)"
      >
        <X size={14} />
      </button>
    </div>
  );
}
